/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';

import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { ToastList } from '@/components/ToastList';
import { getNonExpiredClosedToasts, saveClosedToast } from '@/utils/localStorage.toasts';
import { hasLowBalance } from '@/utils/hasLowBalance';
import { useGracePeriodToast } from '@/hooks/useGracePeriodToast';
import { useAmlVerificationToast } from '@/hooks/useAmlVerificationToast';
import { useUpdateContactInfoToast } from '@/hooks/useUpdateContactInfoToast';
import { IS_SWEDEN } from '@/constants/constants';
import { selectUserCurrency } from '@/store/selectors/userSelectors';
import { useStateUpdateToast } from '@/hooks/useStateUpdateToast';
import { selectAutoLoginFinished } from '@/store/selectors/autoLoginSelectors';
import { usePhotoIdToast } from '@/hooks/usePhotoIdToast';
import { useResponsibleGamingToast } from '@/hooks/useResponsibleGamingToast';
import { useKYCToast } from '@/hooks/useKYCToast';
import {
  SMS_MAIL_REMINDER_TOAST_ID,
  useSmsMailReminderToast,
} from '@/hooks/useSmsMailReminderToast';
import { useCashierShow } from '@/features/cashier/useCashier';
import { GTagEvents, triggerGTag } from '@/lib/gTagManager';
import { usePsaStore } from '@/store/zustand/psa-store';
import { selectUserBalance, selectUserDepositCount } from '@/store/wallet/selectors';
import { selectIsAuthenticated } from '@/store/selectors/authSelectors';

const StyledToastsContainer = styled.div`
  position: absolute;
  z-index: 50;
  top: 80px;
  right: 36px;

  @media (max-width: ${props => props.theme.breakpoints.small}) {
    top: 1rem;
    left: 16px;
    right: 16px;
  }
`;

export function ToastsContainer() {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const depositCount = useSelector(selectUserDepositCount);
  const intl = useIntl();
  const autoLoginFinished = useSelector(selectAutoLoginFinished);
  const { psaMessage } = usePsaStore();

  const [toastMessages, setToastMessages] = useState({});
  const [previouslyClosedToasts, setPreviouslyClosedToasts] = useState(getNonExpiredClosedToasts());
  const walletTotalBalance = useSelector(selectUserBalance);
  const currency = useSelector(selectUserCurrency);
  const showCashier = useCashierShow();

  const toastsToDisplay = React.useMemo(
    () =>
      Object.values(toastMessages).filter(toast => previouslyClosedToasts.indexOf(toast.id) === -1),
    [toastMessages, previouslyClosedToasts],
  );

  const doesToastExist = id => !!toastMessages[id];

  const closeToast = toastId => () => {
    // Closed toasts renew in 7 days, but we set
    // the SMS/Mail reminder toast to renew in 2 weeks
    switch (toastId) {
      case SMS_MAIL_REMINDER_TOAST_ID:
        saveClosedToast(toastId, 14);
        break;
      default:
        saveClosedToast(toastId, 7);
        break;
    }

    setPreviouslyClosedToasts(getNonExpiredClosedToasts());
  };

  const addToastMessage = message => {
    setToastMessages(prevState => ({ ...prevState, [message.id]: message }));
  };

  const cleanupToastMessages = key => {
    setToastMessages(prevState => {
      const remainingKeys = Object.keys(prevState).filter(k => k.indexOf(key) === -1);
      return remainingKeys.reduce((a, i) => ({ ...a, [i]: prevState[i] }), {});
    });
  };

  useGracePeriodToast(addToastMessage, cleanupToastMessages);
  useAmlVerificationToast(addToastMessage, cleanupToastMessages);
  useUpdateContactInfoToast(addToastMessage, cleanupToastMessages);
  useStateUpdateToast(addToastMessage, cleanupToastMessages);
  usePhotoIdToast(addToastMessage, cleanupToastMessages);
  useResponsibleGamingToast(addToastMessage, cleanupToastMessages);
  useKYCToast(addToastMessage, cleanupToastMessages);
  useSmsMailReminderToast(addToastMessage, cleanupToastMessages, closeToast);
  // We comment this for now so the switch toast doesnt show before we release .ca
  // useOntarioSwitchToast(addToastMessage, cleanupToastMessages);

  useEffect(() => {
    const lowBalanceToast = 'low-balance';

    if (
      !isAuthenticated ||
      !depositCount ||
      walletTotalBalance === null ||
      !hasLowBalance(depositCount, walletTotalBalance, currency)
    ) {
      cleanupToastMessages(lowBalanceToast);
      return;
    }

    if (doesToastExist(lowBalanceToast)) {
      return;
    }

    // add the toast if it already doesn't exist
    const toast = {
      id: lowBalanceToast,
      order: 1,
      title: intl.formatMessage({ id: 'notifications.lowBalanceTitle' }),
      subtitle: intl.formatMessage({ id: 'notifications.lowBalanceSubtitle' }),
      cta: intl.formatMessage({ id: 'notifications.lowBalanceCta' }),
      type: 'cta',
      clickCallback: () => {
        triggerGTag(GTagEvents.low_balance_cta);
        showCashier();
      },
    };

    addToastMessage(toast);
    triggerGTag(GTagEvents.low_balance_view);
  }, [isAuthenticated, depositCount, walletTotalBalance, currency]);

  return (
    <StyledToastsContainer
      shouldOffset={!isAuthenticated}
      isSweden={IS_SWEDEN}
      psaShown={psaMessage}
    >
      {autoLoginFinished && <ToastList messages={toastsToDisplay} />}
    </StyledToastsContainer>
  );
}
